import { IdAware, LoopingIndexedMap } from "game-common/models"
import { Callback } from "game-common/util"
import { Loader, LoaderResource, Texture, utils } from "pixi.js"
import { lpcSpriteSheets } from "../../../../lpc-spritesheets"
import { logger } from "../../../../logger"

export type LoadingLifecycle = "loading" | "loaded" | "failed"

interface LoadingState {
    url: string
    lifeCycle: LoadingLifecycle
    callbacks: Callback<string>[]
    // How many times we've dispatched this url to a loader. Bounds retries so a permanently
    // missing/broken sheet can't retry-loop forever.
    attempts: number
}

interface LoadingMeta {
    url: string
    callback: Function
}

// A sheet that errors (404, transient network failure) is retried on another loader up to this
// many times before we give up and unblock waiting entities so they render available layers.
const MAX_LOAD_ATTEMPTS = 3

// Base backoff between retries (multiplied by attempt count).
const RETRY_DELAY_MS = 500

export class LoaderContainer implements IdAware {
    static loaderNumber = 0

    // Upper bound on how long a single loader.load() batch may run before we assume it is wedged
    // (a stalled request PIXI never resolves) and hand the batch back for retry elsewhere, so one
    // hung fetch can't leave isLoading stuck true and poison this container for the session.
    static loadTimeoutMs = 30000

    entityId: string
    loader: Loader
    timer: any
    watchdog: any
    isLoading: boolean
    buffer: LoadingMeta[] = []
    private inFlight: LoadingMeta[] = []

    // Invoked with the in-flight items when a batch exceeds loadTimeoutMs without completing. The
    // owner (SpriteSheetLoader) uses it to fail + retry those urls on a different loader.
    onStall?: (items: LoadingMeta[]) => void

    constructor() {
        this.entityId = "loader-" + ++LoaderContainer.loaderNumber
        this.loader = new Loader()
    }

    add = (toLoad: LoadingMeta) => {
        this.buffer.push(toLoad)
        this.scheduleFlush()
    }

    // (Re)arm the debounce that drains the buffer into a single loader.load() call. A burst of
    // adds within the window batches into one load. No-op while a load is in flight -- the load's
    // completion handler re-drains anything that arrived meanwhile (see flush), so requests can
    // never be stranded in the buffer waiting for an incidental future add().
    private scheduleFlush = () => {
        if (this.isLoading || this.loader.loading) {
            return
        }
        if (this.buffer.length < 1) {
            return
        }
        if (this.timer) {
            clearTimeout(this.timer)
        }
        this.timer = setTimeout(this.flush, 100)
    }

    private flush = () => {
        this.timer = undefined

        // Never add to a PIXI loader that is mid-run; the completion handler will re-drain.
        if (this.isLoading || this.loader.loading || this.buffer.length < 1) {
            return
        }

        const batch = this.buffer
        this.buffer = []
        this.inFlight = batch
        batch.forEach(item => {
            const { url, callback } = item
            this.loader.add(url, url, callback)
        })

        this.isLoading = true

        // Guards the batch against being settled twice (watchdog firing vs. the real completion).
        let settled = false
        const finish = () => {
            settled = true
            if (this.watchdog) {
                clearTimeout(this.watchdog)
                this.watchdog = undefined
            }
            this.isLoading = false
            this.inFlight = []
            // Items added while this batch was loading were held in the buffer; kick them off now
            // instead of waiting for the next incidental add() to happen to hit this loader idle.
            if (this.buffer.length > 0) {
                this.scheduleFlush()
            }
        }

        this.watchdog = setTimeout(() => {
            if (settled) {
                return
            }
            const stalled = this.inFlight
            finish()
            this.onStall?.(stalled)
        }, LoaderContainer.loadTimeoutMs)

        this.loader.load(() => {
            if (settled) {
                return
            }
            finish()
        })
    }
}

export interface LoaderContainer {
    entityId: string
    loader: Loader
}

export class SpriteSheetLoader {
    sheetLoadingState: Record<string, LoadingState> = {}
    loaders: LoopingIndexedMap<LoaderContainer> = new LoopingIndexedMap()
    urlLoaderMap: Map<string, Loader> = new Map()
    private loadedTextures: Set<string> = new Set()

    constructor() {
        for (let i = 0; i < 8; i++) {
            const container = new LoaderContainer()
            // A batch that hangs past the watchdog is failed + retried on another loader.
            container.onStall = items => items.forEach(item => this.handleStalled(item))
            this.loaders.push(container)
        }

        // Suppress PIXI texture cache warnings
        // this.suppressTextureCacheWarnings()
    }

    // Route a stalled in-flight request through the same failure path as a real load error, so it
    // is retried on a fresh loader instead of hanging forever behind a wedged one.
    private handleStalled = (item: LoadingMeta) => {
        logger.warn({ tags: ["assets", "loading"] }, "Sheet load stalled; retrying", item.url)
        this.loadCallback({ url: item.url, error: new Error("sheet load timeout") } as any)
    }

    private suppressTextureCacheWarnings = () => {
        const originalWarn = console.warn
        console.warn = (...args) => {
            // Suppress PIXI texture cache warnings
            if (args[0] && typeof args[0] === "string" && args[0].includes("Texture added to the cache with an id")) {
                return
            }
            // Allow other warnings through
            originalWarn.apply(console, args)
        }
    }

    bulkLoad = (urls: string[], completeCallback: Callback<void>) => {
        let descriptorCountdown = lpcSpriteSheets.length
        const s = Date.now()
        urls.forEach(url => {
            this.load(url, _ => {
                descriptorCountdown -= 1
                if (descriptorCountdown <= 0) {
                    logger.info({ tags: ["assets", "loading"] }, "Bulk asset load completed", urls.length)
                    completeCallback()
                }
            })
        })
    }

    getLoaderFor = (url: string): Loader => {
        return this.urlLoaderMap.get(url)
    }

    load = (url: string, callback: Callback<string>) => {
        const key = `${url}`

        const loadingState = this.sheetLoadingState[key]

        // its not yet been queued for loading; setup the sheet for loading
        // add the callback
        // register loader callback
        if (!loadingState) {
            this.sheetLoadingState[key] = {
                url,
                lifeCycle: "loading",
                callbacks: [callback],
                attempts: 1,
            }
            this.dispatch(url)
            return
        }

        // its already been loaded; callback immediately
        if (loadingState.lifeCycle === "loaded") {
            callback(url)
            return
        }

        // a previous attempt failed and is currently idle; piggyback and re-issue the load
        if (loadingState.lifeCycle === "failed") {
            loadingState.callbacks.push(callback)
            this.retry(url)
            return
        }

        // queue the callback
        loadingState.callbacks.push(callback)
    }

    // (Re)assign the url to the next round-robin loader and enqueue it. Round-robin advances each
    // call, so a retry almost always lands on a loader that doesn't already hold the failed
    // resource (avoiding PIXI's "resource already exists" no-op reload).
    private dispatch = (url: string) => {
        const loaderContainer = this.loaders.next()
        this.urlLoaderMap.set(url, loaderContainer.loader)
        loaderContainer.add({
            url,
            callback: this.loadCallback,
        })
    }

    loadCallback = (resource: LoaderResource) => {
        const url = resource.url
        const loadingState = this.sheetLoadingState[url]
        if (!loadingState) {
            return
        }

        // Already settled (e.g. a wedged batch was retried elsewhere and succeeded, then the
        // original finally errored in): ignore the late duplicate.
        if (loadingState.lifeCycle === "loaded") {
            return
        }

        // A failed fetch still fires this callback (PIXI sets resource.error); our watchdog also
        // routes stalls here with a synthetic error. Don't mark such a sheet "loaded" with no
        // textures -- retry it, or (once exhausted) unblock waiters so they render what did load.
        if ((resource as any).error) {
            this.onLoadFailed(url, loadingState)
            return
        }

        // Check for duplicate textures and prevent cache warnings
        this.validateTextureCache(resource)

        // fullfill the waiting callbacks
        loadingState.callbacks.forEach(callback => {
            callback(loadingState.url)
        })

        // clear them out, mark resource as loaded
        loadingState.callbacks = []
        loadingState.lifeCycle = "loaded"
    }

    private onLoadFailed = (url: string, loadingState: LoadingState) => {
        if (loadingState.attempts < MAX_LOAD_ATTEMPTS) {
            loadingState.lifeCycle = "failed"
            logger.warn(
                { tags: ["assets", "loading"] },
                "Sheet load failed; scheduling retry",
                url,
                loadingState.attempts,
            )
            setTimeout(() => this.retry(url), RETRY_DELAY_MS * loadingState.attempts)
            return
        }

        // Exhausted retries. Unblock any waiting entities (fire their callbacks) so they render
        // with the layers that did load rather than staying fully blank forever, and mark the
        // sheet terminal so it never retries again.
        logger.error({ tags: ["assets", "loading"] }, "Sheet load failed permanently; unblocking waiters", url)
        loadingState.callbacks.forEach(callback => {
            callback(loadingState.url)
        })
        loadingState.callbacks = []
        loadingState.lifeCycle = "loaded"
    }

    private retry = (url: string) => {
        const loadingState = this.sheetLoadingState[url]
        // Only retry from an idle failed state; a retry already in flight (or a success) wins.
        if (!loadingState || loadingState.lifeCycle !== "failed") {
            return
        }
        loadingState.attempts += 1
        loadingState.lifeCycle = "loading"
        this.dispatch(url)
    }

    private validateTextureCache = (resource: LoaderResource) => {
        if (!resource.spritesheet || !resource.spritesheet.textures) {
            return
        }

        const textures = resource.spritesheet.textures
        Object.keys(textures).forEach(textureId => {
            // Mark as loaded
            this.loadedTextures.add(textureId)
        })
    }

    // Method to check if a texture is already loaded
    isTextureLoaded = (textureId: string): boolean => {
        return this.loadedTextures.has(textureId)
    }

    // Method to clear texture tracking (useful for cleanup)
    clearTextureTracking = () => {
        this.loadedTextures.clear()
    }

    // Method to restore original console.warn (useful for debugging)
    restoreConsoleWarn = () => {
        // This would need to store the original warn function
        // For now, we'll keep the suppression active
    }
}

const spriteSheetLoader = new SpriteSheetLoader()
export { spriteSheetLoader }
